import React from "react";
import { CompanyAccess, PermissionScope, RolePermissionInput } from "@me-team/host/main/graphql/types";
import {
    PermissionAction,
    PermissionCategory,
    PermissionModule,
    PermissionScopeOption,
    RolePermissionsResponse,
} from "../interfaces";
import { Mode } from "../RoleDetailsPage";

type CreateCustomRoleInput = {
    name: string;
    description: string;
    permissions: RolePermissionInput[];
};

export class RoleDetailsPageService {
    public static getPageTitle = (mode: Mode): string => {
        switch (mode) {
            case "create": {
                return "roles.createRole";
            }
            case "edit": {
                return "Edit role";
            }
            case "view": {
                return "View role";
            }
            case "clone": {
                return "Clone role";
            }
            case "restore": {
                return "Restore role";
            }
        }
    };

    public static clearSpaces = (value: string): string => {
        return value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
    };

    public static renderLabelWithArrow = (text: string): React.ReactNode => {
        return text.split("&").map((part, index, array) => (
            <React.Fragment key={index}>
                {part.trim()}
                {index < array.length - 1 && <i className="bi bi-arrow-right mx-2"></i>}
            </React.Fragment>
        ));
    };

    public static findActionByCode = (actions: PermissionAction[], actionCode: number): PermissionAction | null => {
        for (const action of actions) {
            if (action.code === actionCode) {
                return action;
            }

            if (action.children.length > 0) {
                const foundChildAction = this.findActionByCode(action.children, actionCode);

                if (foundChildAction) {
                    return foundChildAction;
                }
            }
        }

        return null;
    };

    public static findActionByCodeInModules = (
        modules: PermissionModule[],
        actionCode: number
    ): PermissionAction | null => {
        for (const module of modules) {
            for (const category of module.categories) {
                const foundAction = this.findActionByCode(category.actions, actionCode);

                if (foundAction) {
                    return foundAction;
                }
            }
        }

        return null;
    };

    public static isActionDependenciesSatisfied = (modules: PermissionModule[], action: PermissionAction): boolean => {
        if (!action.dependsOn || action.dependsOn.length === 0) {
            return true;
        }

        return action.dependsOn.every((dependencyCode) => {
            const dependencyAction = this.findActionByCodeInModules(modules, dependencyCode);

            return Boolean(dependencyAction?.isActive);
        });
    };

    public static isCategoryDependenciesSatisfied = (
        modules: PermissionModule[],
        category: PermissionCategory
    ): boolean => {
        if (!category.actions.length) {
            return true;
        }

        return category.actions.every((action) => this.isActionDependenciesSatisfied(modules, action));
    };

    private static resetScopeToSelf = (scope: PermissionScopeOption[] | null): PermissionScopeOption[] | null => {
        if (!scope || !scope.length) return scope;

        return scope.map((option) => ({
            ...option,
            isActive: String(option.value).toUpperCase() === PermissionScope.Self,
        }));
    };

    public static cascadeUnsatisfiedDependencies = (modules: PermissionModule[]): PermissionModule[] => {
        let current = modules;
        let changed = true;

        while (changed) {
            changed = false;

            const next = current.map((module) => ({
                ...module,
                categories: module.categories.map((category) => ({
                    ...category,
                    actions: category.actions.map((action) => {
                        if (action.isActive && !this.isActionDependenciesSatisfied(current, action)) {
                            changed = true;

                            return {
                                ...action,
                                isActive: false,
                                scope: this.resetScopeToSelf(action.scope),
                                children: action.children.map((child) => ({
                                    ...child,
                                    isActive: false,
                                    scope: this.resetScopeToSelf(child.scope),
                                })),
                            };
                        }

                        return action;
                    }),
                })),
            }));

            current = next;
        }

        return current;
    };

    public static getActiveScope = (action: PermissionAction): PermissionScope | undefined => {
        if (!action.supportsScope || !action.scope || action.scope.length === 0) {
            return undefined;
        }

        const activeScope = action.scope.find((scopeOption) => scopeOption.isActive);

        return activeScope?.value.toUpperCase() as PermissionScope;
    };

    public static buildRolePermissionInput = (action: PermissionAction): RolePermissionInput => {
        const permission: RolePermissionInput = {
            action: action.name as CompanyAccess,
        };

        const activeScope = this.getActiveScope(action);

        if (activeScope) {
            permission.scope = activeScope;
        }

        return permission;
    };

    public static collectActivePermissionsFromActions = (actions: PermissionAction[]): RolePermissionInput[] => {
        return actions.flatMap((action) => {
            const currentActionPermissions: RolePermissionInput[] = [];

            if (action.isActive) {
                currentActionPermissions.push(this.buildRolePermissionInput(action));
            }

            const childrenPermissions =
                action.children.length > 0 ? this.collectActivePermissionsFromActions(action.children) : [];

            return [...currentActionPermissions, ...childrenPermissions];
        });
    };

    public static collectActivePermissionsFromCategories = (
        categories: PermissionCategory[]
    ): RolePermissionInput[] => {
        return categories.flatMap((category) => {
            const currentCategoryPermissions = this.collectActivePermissionsFromActions(category.actions);

            const subCategoryPermissions =
                category.subCategories.length > 0
                    ? this.collectActivePermissionsFromCategories(category.subCategories)
                    : [];

            return [...currentCategoryPermissions, ...subCategoryPermissions];
        });
    };

    public static buildCreateCustomRoleInput = (permissionsData: RolePermissionsResponse): CreateCustomRoleInput => {
        return {
            name: permissionsData.role_info.name?.trim() || "",
            description: permissionsData.role_info.description || "",
            permissions: this.collectActivePermissionsFromCategories(
                permissionsData.modules.flatMap((module) => module.categories)
            ),
        };
    };
}
